import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "@/components/common/States";
import { api } from "@/lib/api";
import { TENANT_STATUS, formatDate } from "@/lib/labels";

export default function SchoolsList() {
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get("q") || "");
  const status = params.get("status") || "all";
  const qText = params.get("q") || "";
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["platform-schools", qText, status],
    queryFn: () => api.get("/platform/schools", { params: { q: qText || undefined, status: status === "all" ? undefined : status } }).then((r) => r.data),
  });
  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k); else next.set(k, v);
    setParams(next);
  };
  return (
    <div>
      <PageHeader eyebrow="Pusat Kendali SIST" title="Daftar Sekolah" description="Cari dan kelola siklus hidup tenant sekolah."
        actions={<Button asChild data-testid="schools-create-button"><Link to="/pusat-kendali/sekolah/baru"><Plus className="h-4 w-4 mr-1" /> Buat Sekolah</Link></Button>} />
      <form className="flex flex-col sm:flex-row gap-3 mb-6" onSubmit={(e) => { e.preventDefault(); update("q", text.trim()); }}>
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input className="pl-9" value={text} onChange={(e) => setText(e.target.value)} placeholder="Cari nama, ID sekolah, NPSN, atau kota" aria-label="Cari sekolah" data-testid="schools-search-input" />
        </div>
        <Select value={status} onValueChange={(v) => update("status", v)}>
          <SelectTrigger className="sm:w-56" aria-label="Filter status" data-testid="schools-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua status</SelectItem>
            {Object.entries(TENANT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="submit" variant="outline" data-testid="schools-search-button">Cari</Button>
      </form>
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : !q.data.length ? (
        <EmptyState title="Tidak ada sekolah" description={qText || status !== "all" ? "Tidak ada sekolah yang cocok. Ubah kata kunci atau filter status." : "Belum ada sekolah terdaftar. Mulai dengan membuat sekolah baru."}
          action={<Button asChild size="sm" data-testid="schools-empty-create-button"><Link to="/pusat-kendali/sekolah/baru">Buat Sekolah</Link></Button>} />
      ) : (
        <>
        <p className="text-sm text-muted-foreground mb-3" data-testid="schools-count">{q.data.length} sekolah ditemukan</p>
        <ul className="md:hidden space-y-3">
          {q.data.map((s) => (
            <li key={s.id}>
              <Link to={`/pusat-kendali/sekolah/${s.id}`} className="block rounded-xl border bg-card p-4 space-y-1 active:bg-muted" data-testid={`school-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2"><p className="font-semibold">{s.name}</p><StatusBadge map={TENANT_STATUS} value={s.status} /></div>
                <p className="text-xs text-muted-foreground">{s.level} · {s.city} · {s.id}</p>
              </Link>
            </li>
          ))}
        </ul>
        <div className="hidden md:block rounded-xl border overflow-x-auto">
          <Table data-testid="schools-table">
            <TableHeader><TableRow><TableHead>ID Sekolah</TableHead><TableHead>Nama</TableHead><TableHead>Jenjang</TableHead><TableHead>Kota</TableHead><TableHead>Status</TableHead><TableHead>Dibuat</TableHead></TableRow></TableHeader>
            <TableBody>
              {q.data.map((s) => (
                <TableRow key={s.id} className="cursor-pointer" onClick={() => navigate(`/pusat-kendali/sekolah/${s.id}`)} data-testid={`school-row-${s.id}`}>
                  <TableCell className="font-mono text-xs">{s.id}</TableCell>
                  <TableCell className="font-semibold">{s.name}</TableCell>
                  <TableCell>{s.level}</TableCell>
                  <TableCell>{s.city}</TableCell>
                  <TableCell><StatusBadge map={TENANT_STATUS} value={s.status} /></TableCell>
                  <TableCell className="text-xs">{formatDate(s.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>
      )}
    </div>
  );
}
